/**
 * InsightCardBlockRenderer
 *
 * Renders a single InsightCard block with its embedded elements in two modes:
 * 1. Two-column mode: When layout.left and layout.right are provided
 * 2. Single-column mode: When layout is not provided or incomplete
 *
 * Aligns with the API contract from fetchInsightReports.
 */

import React from 'react';
import { InsightCard } from '@strivelabs/ui-blocks';
import type {
  InsightCardBlock,
  BlockElement,
  UIActionEvent,
} from '@/types/ui-blocks';
import { dispatchBlock } from './block-renderer-utils';

export interface InsightCardBlockRendererProps {
  /** Insight card block with layout, presentation, and data */
  block: InsightCardBlock;
  /** Callback fired when user interacts with UI components */
  onUIAction?: (event: UIActionEvent) => void;
}

/**
 * Converts a BlockElement to a UIBlock-compatible format for dispatchBlock
 */
const elementToBlock = (element: BlockElement) => {
  return {
    type: element.type,
    version: element.version,
    content: element.content,
  };
};

/**
 * Renders a list of elements by their IDs using dispatchBlock
 */
const renderElements = (
  elementIds: string[],
  elements: BlockElement[],
  onUIAction: (event: UIActionEvent) => void
): React.ReactNode => {
  // Create a map for quick lookup
  const elementMap = new Map(elements.map((el) => [el.id, el]));

  return elementIds.map((id) => {
    const element = elementMap.get(id);
    if (!element) {
      return (
        <div key={id} className="text-sm text-destructive">
          Element not found: {id}
        </div>
      );
    }

    const block = elementToBlock(element);
    return (
      <div key={id}>
        {dispatchBlock({
          block: block as Parameters<typeof dispatchBlock>[0]['block'],
          onUIAction,
        })}
      </div>
    );
  });
};

export const InsightCardBlockRenderer: React.FC<InsightCardBlockRendererProps> = ({
  block,
  onUIAction = () => {},
}) => {
  // Extract config properties from the block
  // API returns: { id, type, version, config: { heading, border_color, layout }, block_elements }
  const { config, block_elements } = block;
  const { tags, heading, heading_badge, border_color, layout } = config;

  // Determine rendering mode based on layout configuration
  const hasTwoColumnLayout = layout?.left && layout?.right &&
                             layout.left.length > 0 && layout.right.length > 0;

  if (hasTwoColumnLayout) {
    // Two-column mode: render left and right slots
    const leftSlot = renderElements(layout.left!, block_elements, onUIAction);
    const rightSlot = renderElements(layout.right!, block_elements, onUIAction);

    return (
      <InsightCard
        tags={tags}
        heading={heading}
        headingBadge={heading_badge}
        borderColor={border_color}
        leftSlot={leftSlot}
        rightSlot={rightSlot}
      />
    );
  } else {
    // Single-column mode: render all block_elements in order, top to bottom
    const allElements = renderElements(
      block_elements.map(el => el.id),
      block_elements,
      onUIAction
    );

    return (
      <InsightCard
        tags={tags}
        heading={heading}
        headingBadge={heading_badge}
        borderColor={border_color}
      >
        {allElements}
      </InsightCard>
    );
  }
};

export default InsightCardBlockRenderer;
